import React, { useEffect, useMemo, useState } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Wrench, Plus, Loader2, MapPin, User, ImageIcon, LayoutGrid, CalendarDays,
} from 'lucide-react';
import { facilityAPI, locationsAPI } from '../lib/api';
import { ReportDialog } from './facility/ReportDialog';
import { TicketDetailSheet } from './facility/TicketDetailSheet';
import { FacilityCalendar } from './facility/FacilityCalendar';
import {
  CATEGORY_LABELS, URGENCY_LABELS, URGENCY_STYLES, STATUS_LABELS, STATUS_STYLES,
  ORDER_TYPE_LABELS, ORDER_TYPE_ORDER, BOARD_COLUMNS, fmtDateDe,
} from './facility/constants';
import { toast } from 'sonner';

const TicketCard = ({ ticket, onClick }) => (
  <Card
    className="p-3 cursor-pointer hover:shadow-md transition-shadow border-border/60"
    onClick={onClick}
    data-testid={`facility-card-${ticket.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="font-medium text-sm line-clamp-2">{ticket.title}</p>
      {ticket.photos?.some((p) => p.kind === 'report') && <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />}
    </div>
    <div className="flex flex-wrap gap-1 mt-2">
      <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">{ORDER_TYPE_LABELS[ticket.order_type] || 'Auftrag'}</Badge>
      <Badge variant="outline" className={`text-[10px] ${URGENCY_STYLES[ticket.urgency]}`}>{URGENCY_LABELS[ticket.urgency]}</Badge>
      {ticket.source === 'employee' && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Meldung</Badge>}
    </div>
    <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
      <p className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ticket.location_name || 'Kein Standort'}{ticket.room_name ? ` · ${ticket.room_name}` : ''}</p>
      <p className="flex items-center gap-1"><User className="h-3 w-3" />{ticket.reporter_name || '–'}</p>
      {ticket.assigned_names?.length > 0 && <p className="flex items-center gap-1"><Wrench className="h-3 w-3" />{ticket.assigned_names.join(', ')}</p>}
      {ticket.scheduled_start && <p>📅 {fmtDateDe(ticket.scheduled_start)}</p>}
    </div>
  </Card>
);

export default function Facility() {
  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState({ technicians: [], can_manage: false, can_work: false });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // Filter
  const [fLocation, setFLocation] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fCategory, setFCategory] = useState('all');
  const [fType, setFType] = useState('all');
  const [fMine, setFMine] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fLocation !== 'all') params.location_id = fLocation;
      if (fStatus !== 'all') params.status = fStatus;
      if (fCategory !== 'all') params.category = fCategory;
      if (fType !== 'all') params.order_type = fType;
      if (fMine) params.mine = true;
      const [tRes, mRes, lRes] = await Promise.all([
        facilityAPI.list(params),
        facilityAPI.meta(),
        locations.length ? Promise.resolve({ data: locations }) : locationsAPI.getAll({ is_active: true }),
      ]);
      setTickets(tRes.data || []);
      if (mRes.data) setMeta(mRes.data);
      if (lRes.data) setLocations(lRes.data);
    } catch {
      toast.error('Fehler beim Laden der Haustechnik-Daten');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [fLocation, fStatus, fCategory, fType, fMine]); // eslint-disable-line

  const openTicket = (t) => { setDetailId(t.id); setDetailOpen(true); };

  const columns = useMemo(() => {
    const map = {};
    BOARD_COLUMNS.forEach((s) => { map[s] = []; });
    tickets.forEach((t) => { if (map[t.status]) map[t.status].push(t); });
    return map;
  }, [tickets]);

  const rejectedCount = tickets.filter((t) => t.status === 'rejected').length;

  return (
    <MaybeLayout>
      <div className="space-y-6" data-testid="facility-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
              <Wrench className="h-6 w-6 text-primary" />
              Haustechnik
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Schäden melden, freigeben und als Aufgaben für die Haustechnik terminieren & dokumentieren.
            </p>
          </div>
          <Button className="rounded-full" onClick={() => setReportOpen(true)} data-testid="facility-report-btn">
            <Plus className="h-4 w-4 mr-2" /> Auftrag anlegen
          </Button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="w-44" data-testid="facility-filter-type"><SelectValue placeholder="Auftragstyp" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              {ORDER_TYPE_ORDER.map((k) => <SelectItem key={k} value={k}>{ORDER_TYPE_LABELS[k]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fLocation} onValueChange={setFLocation}>
            <SelectTrigger className="w-48" data-testid="facility-filter-location"><SelectValue placeholder="Standort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Standorte</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-44" data-testid="facility-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fCategory} onValueChange={setFCategory}>
            <SelectTrigger className="w-40" data-testid="facility-filter-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={fMine ? 'default' : 'outline'} size="sm" className="rounded-full" onClick={() => setFMine((v) => !v)} data-testid="facility-filter-mine">
            Nur meine
          </Button>
          {rejectedCount > 0 && fStatus === 'all' && (
            <button className="text-xs text-muted-foreground underline" onClick={() => setFStatus('rejected')} data-testid="facility-show-rejected">
              {rejectedCount} abgelehnt
            </button>
          )}
          {meta.can_manage && (
            <div className="ml-auto flex items-center gap-2" data-testid="facility-reporting-setting">
              <Switch
                id="fac-reporting"
                checked={!!meta.employee_reporting_enabled}
                onCheckedChange={async (v) => {
                  try {
                    const r = await facilityAPI.updateSettings({ employee_reporting_enabled: v });
                    setMeta((m) => ({ ...m, employee_reporting_enabled: r.data.employee_reporting_enabled }));
                    toast.success(v ? 'Mitarbeiter-Meldungen aktiviert' : 'Mitarbeiter-Meldungen deaktiviert');
                  } catch { toast.error('Einstellung konnte nicht gespeichert werden'); }
                }}
              />
              <Label htmlFor="fac-reporting" className="text-xs text-muted-foreground cursor-pointer">Mitarbeiter dürfen melden</Label>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <Tabs defaultValue="board">
            <TabsList data-testid="facility-tabs">
              <TabsTrigger value="board" data-testid="facility-tab-board"><LayoutGrid className="h-4 w-4 mr-1" /> Board</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="facility-tab-calendar"><CalendarDays className="h-4 w-4 mr-1" /> Kalender</TabsTrigger>
            </TabsList>

            <TabsContent value="board" className="mt-4">
              {fStatus === 'rejected' ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {tickets.map((t) => <TicketCard key={t.id} ticket={t} onClick={() => openTicket(t)} />)}
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
                  {BOARD_COLUMNS.map((s) => (
                    <div key={s} className="space-y-2" data-testid={`facility-column-${s}`}>
                      <div className="flex items-center gap-2 sticky top-0">
                        <Badge className={STATUS_STYLES[s]}>{STATUS_LABELS[s]}</Badge>
                        <span className="text-xs text-muted-foreground">{columns[s].length}</span>
                      </div>
                      <div className="space-y-2 min-h-[60px]">
                        {columns[s].map((t) => <TicketCard key={t.id} ticket={t} onClick={() => openTicket(t)} />)}
                        {columns[s].length === 0 && <p className="text-xs text-muted-foreground/60 py-2 text-center">–</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tickets.length === 0 && (
                <div className="p-10 text-center text-muted-foreground" data-testid="facility-empty">
                  <Wrench className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>Noch keine Meldungen.</p>
                  <Button variant="link" onClick={() => setReportOpen(true)}>Ersten Schaden melden</Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="calendar" className="mt-4">
              <FacilityCalendar tickets={tickets} onOpenTicket={openTicket} onChanged={load} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        locations={locations}
        defaultLocationId={fLocation !== 'all' ? fLocation : ''}
        onCreated={load}
      />
      <TicketDetailSheet
        ticketId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        meta={meta}
        technicians={meta.technicians || []}
        onChanged={load}
      />
    </MaybeLayout>
  );
}
